import axios from 'axios';

// Simple in-memory cache for throttling requests
const requestCache = new Map<string, {
  timestamp: number;
  data: any;
}>();

// Cache TTL in ms
const CACHE_TTL = 1000 * 60; // 1 minute
const THROTTLE_INTERVAL = 1000; // 1 second between identical calls

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// Create single API instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request cache/throttling to avoid rate limits
apiClient.interceptors.request.use(
  async (config) => {
    // Only throttle GET requests
    if (config.method?.toLowerCase() === 'get') {
      const cacheKey = `${config.method}_${config.url}_${JSON.stringify(config.params || {})}`;
      const cachedResponse = requestCache.get(cacheKey);
      const now = Date.now();
      
      // If we have a cached response that's still valid
      if (cachedResponse) {
        // Check if the request was made recently (throttling)
        const timeSinceLastRequest = now - cachedResponse.timestamp;
        
        if (timeSinceLastRequest < THROTTLE_INTERVAL) {
          console.log(`Throttled request: ${config.url} (made ${timeSinceLastRequest}ms ago)`);
          
          // Two options:
          // 1. Return cached data (shown here)
          // 2. Delay the request (for real-time importance)
          
          // For simplicity, return cached data during the throttle window
          if (cachedResponse.data) {
            return Promise.reject({
              __throttled: true,
              config,
              cachedData: cachedResponse.data,
              message: 'Request throttled, returning cached data'
            });
          }
        }
      }
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Authentication interceptor
apiClient.interceptors.request.use(
  (config) => {
    try {
      // First try businessAuth, then fallback to auth for backwards compatibility
      const businessAuth = JSON.parse(localStorage.getItem('businessAuth') || '{}');
      const publicAuth = JSON.parse(localStorage.getItem('auth') || '{}');
      const auth = businessAuth.token ? businessAuth : publicAuth;
      const token = auth.token;
      
      // 🔍 ENHANCED AUTH TOKEN DEBUGGING
      console.log('🔍 [API AUTH] Adding authentication to request:', {
        url: config.url,
        method: config.method,
        hasBusinessAuth: !!businessAuth.token,
        hasPublicAuth: !!publicAuth.token,
        usingAuth: businessAuth.token ? 'businessAuth' : (publicAuth.token ? 'publicAuth' : 'none'),
        tokenPreview: token ? token.substring(0, 20) + '...' : 'None',
        tokenLength: token ? token.length : 0,
        authData: {
          businessAuth: {
            hasToken: !!businessAuth.token,
            hasUser: !!businessAuth.user,
            userRole: businessAuth.user?.role || 'none'
          },
          publicAuth: {
            hasToken: !!publicAuth.token,
            hasUser: !!publicAuth.user,
            userRole: publicAuth.user?.role || 'none'
          }
        },
        timestamp: new Date().toISOString()
      });
      
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
        console.log('✅ [API AUTH] Authorization header added to request');
      } else {
        console.warn('⚠️ [API AUTH] No token available for request');
      }
    } catch (error) {
      console.error('🚨 [API AUTH] Error adding auth token to request:', error);
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle throttled requests and caching
apiClient.interceptors.response.use(
  (response) => {
    // Cache successful GET responses
    if (response.config.method?.toLowerCase() === 'get') {
      const cacheKey = `${response.config.method}_${response.config.url}_${JSON.stringify(response.config.params || {})}`;
      
      // Store in cache
      requestCache.set(cacheKey, {
        timestamp: Date.now(),
        data: response.data
      });
      
      // Clean up old cache entries (not done on every response to save performance)
      if (Math.random() < 0.1) { // 10% chance to clean cache
        const now = Date.now();
        for (const [key, value] of requestCache.entries()) {
          if (now - value.timestamp > CACHE_TTL) {
            requestCache.delete(key);
          }
        }
      }
    }
    
    return response;
  },
  (error) => {
    // Check if this is a throttled request with cached data
    if (error.__throttled) {
      console.log('Returning cached data for throttled request');
      return {
        ...error.config,
        status: 200,
        statusText: 'OK (Cached)',
        data: error.cachedData
      };
    }
    
    // Error handling for 429 Too Many Requests
    if (error.response?.status === 429) {
      console.warn('Rate limit hit - you are making too many requests. Implement backoff strategy.');
      
      // Try to find cached data for this request
      if (error.config.method?.toLowerCase() === 'get') {
        const cacheKey = `${error.config.method}_${error.config.url}_${JSON.stringify(error.config.params || {})}`;
        const cachedResponse = requestCache.get(cacheKey);
        
        // Return cached data if available, despite being expired
        if (cachedResponse?.data) {
          console.log('Returning stale cached data due to rate limiting');
          return {
            ...error.config,
            status: 200,
            statusText: 'OK (Stale Cache)',
            data: cachedResponse.data
          };
        }
      }
    }
    
    // Handle unauthorized access
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      if (currentPath.startsWith('/business')) {
        console.warn('Authentication required for business endpoints. You may need to log in again.');
      }
    }
    
    return Promise.reject(error);
  }
);

// Debug interceptors (optional for development)
if (import.meta.env.DEV) {
  apiClient.interceptors.request.use(
    (config) => {
      const shouldLog = localStorage.getItem('ENABLE_API_LOGGING') === 'true';
      if (shouldLog) {
        const requestId = Date.now().toString(36) + Math.random().toString(36).substring(2, 5);
        console.log(`🔄 [API Request ${requestId}] ${config.method?.toUpperCase()} ${config.url}`, {
          params: config.params,
          data: config.data
        });
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  apiClient.interceptors.response.use(
    (response) => {
      const shouldLog = localStorage.getItem('ENABLE_API_LOGGING') === 'true';
      if (shouldLog) {
        const logId = Date.now().toString(36);
        console.log(`✅ [API Response ${logId}] ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`, {
          data: response.data
        });
      }
      return response;
    },
    (error) => {
      if (error.response) {
        const logId = Date.now().toString(36);
        console.error(`❌ [API Error ${logId}] ${error.response.status} ${error.config.method?.toUpperCase()} ${error.config.url}`, {
          status: error.response.status,
          message: error.message,
          data: error.response.data
        });
      } else if (error.request) {
        console.error('❌ API Error - No Response:', { 
          request: error.request, 
          message: error.message 
        });
      } else {
        console.error('❌ API Setup Error:', error.message);
      }
      return Promise.reject(error);
    }
  );
}

// Export type for API responses (for consistency)
export type ApiResponse<T> = 
  | { success: true; data: T }
  | { success: false; error: string };

// Utility function to handle API errors
export const handleApiError = (error: any): ApiResponse<any> => {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 401) {
      return { success: false, error: 'Authentication required' };
    }
    if (error.response?.status === 429) {
      return { success: false, error: 'Too many requests, please try again later' };
    }
    return { success: false, error: error.response?.data?.message || error.message };
  }
  return { success: false, error: 'An unexpected error occurred' };
}; 